/**
 * Appointment Time Utilities
 *
 * SINGLE SOURCE OF TRUTH for all appointment time formatting and conversion.
 *
 * ─── Authoring Contract ────────────────────────────────────────────────────
 * • toZonedTime, fromZonedTime, and format (from date-fns-tz) are ONLY
 *   called in this file. No other file may import them directly.
 *
 * ─── Input Contract ────────────────────────────────────────────────────────
 * • utcStr arguments represent UTC instants. The API may omit the trailing
 *   'Z' because appointment columns are PostgreSQL `timestamp without time
 *   zone`; normalizeAppointmentUtc() restores it before JavaScript parsing.
 * • Do NOT pass: locale strings, date-only strings, ambiguous local strings,
 *   or already-converted values.
 * • Timezone is NEVER inferred from the string itself.
 *
 * ─── Timezone Contract ─────────────────────────────────────────────────────
 * • Every exported function validates timeZoneId via safeTimeZone().
 * • Invalid or missing timeZoneId falls back to 'UTC' — NEVER browser local.
 * • Appointment API responses resolve timeZoneId from the owning practice's
 *   current setting. Use that response value; do not replace it with the browser
 *   or a portal user's account timezone.
 *
 * ─── Time Format Contract ──────────────────────────────────────────────────
 * • formatApptTime and formatApptDateTime accept an optional is24Hour param.
 * • When is24Hour is not supplied, the user's saved preference is read from
 *   timeFormatPreference (localStorage-backed, defaults to 12h).
 * • Callers may override with an explicit boolean when needed.
 *
 * ─── FullCalendar Notes ────────────────────────────────────────────────────
 * • NEVER pass Date objects or raw UTC strings (ending in 'Z') to FullCalendar
 *   appointment events — FC re-converts them using browser local timezone.
 * • ALWAYS use toFloatingTimeString() for FullCalendar start/end values.
 * • If drag/resize is ever enabled: event.start / event.end will be Date
 *   objects in browser local time. Convert them back to UTC with:
 *     toUtcFromZoned(event.start.toISOString().slice(0,19), appointment.timeZoneId)
 *   Never send raw Date values to the backend.
 *
 * ─── Lint / PR Guard ───────────────────────────────────────────────────────
 * • toLocaleString() / toLocaleTimeString() / toLocaleDateString() without
 *   an explicit { timeZone } option MUST NOT appear in appointment-time paths.
 * • PRs introducing these patterns in appointment context should be rejected.
 */
import { toZonedTime, fromZonedTime, format } from 'date-fns-tz';
import { getIs24Hour } from './timeFormatPreference';
import { normalizeAppointmentUtc } from './appointmentUtc';

export { normalizeAppointmentUtc } from './appointmentUtc';

// ---------------------------------------------------------------------------
// Format constants
// ---------------------------------------------------------------------------

/** 12-hour time with timezone abbreviation — "2:00 PM EDT" */
export const TIME_FORMAT_12H = 'h:mm aa zzz';

/** 24-hour time with timezone abbreviation — "14:00 EDT" */
export const TIME_FORMAT_24H = 'HH:mm zzz';

/**
 * @deprecated Use TIME_FORMAT_12H. Kept for backward compatibility.
 * Will be removed in a future cleanup.
 */
export const TIME_FORMAT = TIME_FORMAT_12H;

/** Date only — "Wednesday, April 15, 2026" */
export const DATE_FORMAT = 'EEEE, MMMM d, yyyy';

/**
 * @deprecated DATETIME_FORMAT is now dynamic based on time preference.
 * Use formatApptDateTime() which reads the preference automatically.
 */
export const DATETIME_FORMAT = "EEEE, MMMM d, yyyy 'at' h:mm aa zzz";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Validates an IANA timezone string using Intl.DateTimeFormat.
 * Returns the original value if valid, 'UTC' if invalid or absent.
 * Called by EVERY exported function — callers must not bypass this.
 *
 * @param {string|null|undefined} timeZoneId
 * @returns {string} Valid IANA timezone string
 */
function safeTimeZone(timeZoneId) {
  if (!timeZoneId) return 'UTC';
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timeZoneId });
    return timeZoneId;
  } catch {
    return 'UTC';
  }
}

/**
 * Returns the appropriate time format string based on the 12/24h preference.
 * Explicit is24Hour param overrides the stored preference.
 *
 * @param {boolean|undefined} is24Hour
 * @returns {string} date-fns-tz format string
 */
function getTimeFormat(is24Hour) {
  const use24 = is24Hour !== undefined ? is24Hour : getIs24Hour();
  return use24 ? TIME_FORMAT_24H : TIME_FORMAT_12H;
}

/**
 * Returns the full date-time format string, incorporating the active time format.
 *
 * @param {boolean|undefined} is24Hour
 * @returns {string} date-fns-tz format string
 */
function getDateTimeFormat(is24Hour) {
  return `EEEE, MMMM d, yyyy 'at' ${getTimeFormat(is24Hour)}`;
}

/**
 * Warns (in development) when a value is not a supported UTC appointment
 * timestamp. Timezone-less API values are supported and normalized internally.
 *
 * @param {string} utcStr
 * @param {string} callerName - function name for the warning message
 */
function warnIfNotUtc(utcStr, callerName) {
  const normalized = normalizeAppointmentUtc(utcStr);
  const hasZone = typeof normalized === 'string' && (
    normalized.endsWith('Z') || /[+-]\d{2}(?::?\d{2})?$/.test(normalized)
  );
  if (process.env.NODE_ENV !== 'production' && utcStr && !hasZone) {
    console.warn(
      `[appointmentTimeUtils] ${callerName}: received an unsupported timestamp "${utcStr}". ` +
      'Expected an ISO 8601 UTC appointment timestamp.'
    );
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Format appointment time for display.
 * Respects the user's 12h/24h preference unless is24Hour is explicitly supplied.
 *
 * Examples:
 *   "2:00 PM EDT"  (12h, default)
 *   "14:00 EDT"    (24h)
 *
 * @param {string} utcStr   - UTC ISO string; API values may omit the trailing 'Z'
 * @param {string|null|undefined} timeZoneId - IANA timezone
 * @param {boolean|undefined} is24Hour       - Override 12/24h preference
 * @returns {string}
 */
export function formatApptTime(utcStr, timeZoneId, is24Hour) {
  warnIfNotUtc(utcStr, 'formatApptTime');
  const tz = safeTimeZone(timeZoneId);
  return format(toZonedTime(new Date(normalizeAppointmentUtc(utcStr)), tz), getTimeFormat(is24Hour), { timeZone: tz });
}

/**
 * Format appointment date for display.
 * Date format is always the same regardless of 12/24h preference.
 *
 * Example: "Wednesday, April 15, 2026"
 *
 * @param {string} utcStr   - UTC ISO string; API values may omit the trailing 'Z'
 * @param {string|null|undefined} timeZoneId - IANA timezone
 * @returns {string}
 */
export function formatApptDate(utcStr, timeZoneId) {
  warnIfNotUtc(utcStr, 'formatApptDate');
  const tz = safeTimeZone(timeZoneId);
  return format(toZonedTime(new Date(normalizeAppointmentUtc(utcStr)), tz), DATE_FORMAT, { timeZone: tz });
}

/**
 * Format appointment date and time for display.
 * Respects the user's 12h/24h preference unless is24Hour is explicitly supplied.
 *
 * Examples:
 *   "Wednesday, April 15, 2026 at 2:00 PM EDT"  (12h, default)
 *   "Wednesday, April 15, 2026 at 14:00 EDT"    (24h)
 *
 * @param {string} utcStr   - UTC ISO string; API values may omit the trailing 'Z'
 * @param {string|null|undefined} timeZoneId - IANA timezone
 * @param {boolean|undefined} is24Hour       - Override 12/24h preference
 * @returns {string}
 */
export function formatApptDateTime(utcStr, timeZoneId, is24Hour) {
  warnIfNotUtc(utcStr, 'formatApptDateTime');
  const tz = safeTimeZone(timeZoneId);
  return format(toZonedTime(new Date(normalizeAppointmentUtc(utcStr)), tz), getDateTimeFormat(is24Hour), { timeZone: tz });
}

/**
 * Convert a UTC string to a floating wall-clock string for FullCalendar or form inputs.
 *
 * Returns "yyyy-MM-dd'T'HH:mm:ss" with NO timezone suffix.
 * FullCalendar treats this as a wall-clock time and places the event at the
 * correct position without any further conversion (timeZone: 'local' mode).
 *
 * Also use this to populate <input type="time"> / <input type="datetime-local">
 * when opening an edit or scheduling form.
 *
 * ⚠ NEVER show this value directly to users — it has no timezone indication.
 *
 * @param {string} utcStr   - UTC ISO string; API values may omit the trailing 'Z'
 * @param {string|null|undefined} timeZoneId - IANA timezone
 * @returns {string} e.g. "2026-04-15T14:00:00"
 */
export function toFloatingTimeString(utcStr, timeZoneId) {
  warnIfNotUtc(utcStr, 'toFloatingTimeString');
  const tz = safeTimeZone(timeZoneId);
  return format(toZonedTime(new Date(normalizeAppointmentUtc(utcStr)), tz), "yyyy-MM-dd'T'HH:mm:ss", { timeZone: tz });
}

/**
 * Convert a zoned wall-clock string back to a UTC ISO string.
 *
 * Use this on EVERY save/submit in edit and scheduling flows.
 * Prevents data corruption when the browser timezone differs from the
 * appointment timezone.
 *
 * If FullCalendar drag/resize is ever enabled, use this to convert
 * event.start / event.end back to UTC before sending to the API:
 *   toUtcFromZoned(event.start.toISOString().slice(0,19), appointment.timeZoneId)
 *
 * @param {string} zonedStr   - Wall-clock datetime string in the appointment timezone
 *   (e.g. "2026-04-15T14:00:00" — the value from a datetime-local input)
 * @param {string|null|undefined} timeZoneId - IANA timezone the wall-clock value is in
 * @returns {string} UTC ISO string ending in 'Z'
 */
export function toUtcFromZoned(zonedStr, timeZoneId) {
  const tz = safeTimeZone(timeZoneId);
  return fromZonedTime(zonedStr, tz).toISOString();
}
